import { t } from 'i18next'
import { DataItem, load } from 'scrivito'

import { showToast } from '@/Components/Toast'
import { ToastMessageType } from '@/Components/Toast/const'
import { Timerecord } from '@/Data/Timerecord/TimerecordDataClass'
import { TimerecordClient } from '@/Data/TimerecordClient/TimerecordClientDataClass'
import { TimerecordOrder } from '@/Data/TimerecordOrder/TimerecordOrderDataClass'
import { TimerecordPosition } from '@/Data/TimerecordPosition/TimerecordPositionDataClass'
import { TimerecordUser } from '@/Data/TimerecordUser/TimerecordUserDataClass'
import { WhoAmI } from '@/Data/WhoAmI/WhoAmIDataItem'
import { ensureString } from '@/utils/ensureString'
import { reportError } from '@/utils/reportError'

export interface TimeRecordViewData {
  user: string
  date: string
  customer: string
  order: string
  workPackage: string
  workDuration: string
  typeOfActivity: string
  description: string
  internalRemarks: string
}

export const deleteTimeRecord = (recordId: string): void => {
  try {
    const record = Timerecord.get(recordId)
    if (record) {
      record.delete()
      showToast(
        ToastMessageType.SUCCESS,
        t('timeTracking.notifications.timeEntryDeleted'),
      )
    }
  } catch (err) {
    reportError(err)
  }
}

export const duplicateTimeRecord = (recordId: string): void => {
  try {
    const record = Timerecord.get(recordId)
    if (record) {
      Timerecord.create({
        description: record.get('description'),
        totalDuration: record.get('totalDuration'),
        client: record.get('client'),
        order: record.get('order'),
        position: record.get('position'),
        internalNote: record.get('internalNote'),
        activityType: record.get('activityType'),
        recordedAt: record.get('recordedAt'),
        user: record.get('user'),
      })
      showToast(
        ToastMessageType.SUCCESS,
        t('timeTracking.notifications.timeEntryDuplicated'),
      )
    }
  } catch (err) {
    reportError(err)
  }
}

export const getTimeRecordViewData = (
  recordId: string,
): Promise<TimeRecordViewData | null> =>
  load(() => {
    const record = Timerecord.get(recordId)
    if (!record) {
      return null
    }

    const userId = ensureString(record.get('user'))
    const clientId = ensureString(record.get('client'))
    const orderId = ensureString(record.get('order'))
    const positionId = ensureString(record.get('position'))

    const userRecord = userId ? TimerecordUser.get(userId) : null
    const clientRecord = clientId ? TimerecordClient.get(clientId) : null
    const orderRecord = orderId ? TimerecordOrder.get(orderId) : null
    const positionRecord = positionId
      ? TimerecordPosition.get(positionId)
      : null

    const userName = userRecord
      ? ensureString(userRecord.get('name'))
      : ensureString(WhoAmI.get('name'))

    const recordedAt = ensureString(record.get('recordedAt'))
    const totalDuration = record.get('totalDuration') as number
    const activityType = ensureString(record.get('activityType'))
    const description = ensureString(record.get('description'))
    const internalNote = ensureString(record.get('internalNote'))

    return {
      user: userName,
      date: recordedAt
        ? new Date(recordedAt).toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          })
        : '',
      customer: clientRecord ? ensureString(clientRecord.get('name')) : '',
      order: orderRecord ? ensureString(orderRecord.get('name')) : '',
      workPackage: positionRecord
        ? ensureString(positionRecord.get('name'))
        : '',
      workDuration: totalDuration ? `${totalDuration}h` : '0h',
      typeOfActivity: activityType,
      description: description,
      internalRemarks: internalNote,
    }
  }).catch((err) => {
    reportError('Failed to load time record details', err)
    return null
  })

export const updateMultipleTimeRecordStatuses = async (
  dataItems: DataItem[],
  newStatus: string,
): Promise<void> => {
  try {
    await Promise.all(
      dataItems.map((item) => item.update({ status: newStatus })),
    )
    showToast(
      ToastMessageType.SUCCESS,
      t('timeTracking.notifications.timeEntriesStatusUpdated', {
        count: dataItems.length,
      }),
    )
  } catch (err) {
    reportError('Failed to update time record statuses', err)
    showToast(
      ToastMessageType.ERROR,
      t('timeTracking.notifications.timeEntriesStatusUpdateFailed'),
    )
    throw err
  }
}
